import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Phone, Mail, Truck, Send, ExternalLink, AlertTriangle, Link2, Copy, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRequestDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Send modal state
  const [sendOpen, setSendOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendLang, setSendLang] = useState("fr");
  const [sendChannel, setSendChannel] = useState("email"); // 'email' | 'link'
  const [result, setResult] = useState(null); // {share_url, email_sent}
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/customer-requests/${requestId}`);
        setReq(data.request);
        setMatches(data.matches);
      } catch { toast.error("Request not found"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [requestId]);

  const openSendModal = () => {
    // Pre-select all matches
    setSelectedIds(new Set(matches.map((m) => m.id)));
    setResult(null);
    setCopied(false);
    setSendChannel((req?.email && !req.email.includes("@noemail")) ? "email" : "link");
    setSendOpen(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doSend = async () => {
    if (selectedIds.size === 0) return toast.error("Select at least one forklift");
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/share-links`, {
        client_name: req.name,
        client_id: req.crm_client_id,
        request_id: req.id,
        forklift_ids: Array.from(selectedIds),
        language: sendLang,
        to_email: sendChannel === "email" ? req.email : undefined,
        send_email: sendChannel === "email",
      });
      setResult(data);
      if (data.email_sent) toast.success(`Email envoyé à ${req.email}`);
      else toast.success("Lien généré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!result?.share_url) return;
    navigator.clipboard.writeText(result.share_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!req) return <div className="p-8 text-center text-gray-500">Request not found</div>;

  const hasContact = (req.email && !req.email.includes("@noemail")) || req.phone;
  const hasEmail = req.email && !req.email.includes("@noemail");

  return (
    <div data-testid="admin-request-detail" className="p-8 max-w-6xl">
      <button onClick={() => navigate("/admin/requests")} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Requests
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">{req.name}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
            {req.phone && <a href={`tel:${req.phone}`} className="flex items-center gap-1 hover:text-gray-900"><Phone className="w-3.5 h-3.5" />{req.phone}</a>}
            {req.email && !req.email.includes("@noemail") && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{req.email}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={req.status === "active" ? "bg-yellow-100 text-yellow-800 border-none" : "bg-gray-100 text-gray-500 border-none"}>
            {req.status}
          </Badge>
          {req.crm_client_id && (
            <button onClick={() => navigate(`/admin/crm/clients/${req.crm_client_id}`)} className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-sm">
              View in CRM
            </button>
          )}
        </div>
      </div>

      {/* Client needs */}
      <div className="bg-white border border-gray-200 rounded-sm p-5 mb-6">
        <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">Client Requirements</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Condition", value: req.condition || "Any" },
            { label: "Engine", value: req.engine_type || "Any" },
            { label: "Mast", value: req.mast_stages ? `${req.mast_stages} stages` : "Any" },
            { label: "Tires", value: req.tire_type || "Any" },
            { label: "Drive", value: req.drive || "Any" },
            { label: "Capacity", value: (req.capacity_min || req.capacity_max) ? `${req.capacity_min || "—"} – ${req.capacity_max || "—"} lbs` : "Any" },
            { label: "Budget", value: req.budget_max ? `$${req.budget_max.toLocaleString()}` : "No limit" },
          ].map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-sm p-2.5">
              <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">{item.label}</p>
              <p className="text-sm text-gray-900 font-medium capitalize">{item.value}</p>
            </div>
          ))}
        </div>
        {req.notes && <p className="text-xs text-gray-600 mt-3 bg-yellow-50 border border-yellow-100 rounded-sm p-2">{req.notes}</p>}
      </div>

      {/* Matching machines */}
      <div className="bg-white border border-gray-200 rounded-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-sm font-bold text-gray-900">
            Recommended Machines ({matches.length})
          </h3>
          {matches.length > 0 && (
            <button
              data-testid="open-send-modal-btn"
              onClick={openSendModal}
              disabled={!hasContact && false /* always allow (link works even without contact) */}
              className="inline-flex items-center gap-1.5 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-4 py-2 rounded-sm text-sm"
            >
              <Send className="w-4 h-4" /> Send to client
            </button>
          )}
        </div>

        {matches.length === 0 ? (
          <div className="text-center py-8">
            <Truck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No matching machines in inventory right now.</p>
            <p className="text-xs text-gray-400 mt-1">Matches will appear when you add inventory that fits this client's criteria.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((f) => (
              <div key={f.id} className="border border-gray-200 rounded-sm p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-24 h-18 bg-gray-100 rounded-sm overflow-hidden flex-shrink-0">
                    {f.image_url ? (
                      <img src={f.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${f.image_url}` : f.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Truck className="w-8 h-8 text-gray-300" /></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-heading text-base font-bold text-gray-900">{f.brand} {f.model}</h4>
                        <Badge className={f.condition === "new" ? "bg-yellow-100 text-yellow-800 border-none text-xs mt-1" : "bg-gray-100 text-gray-600 border-none text-xs mt-1"}>
                          {f.condition}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-lg font-bold text-gray-900">${f.price?.toLocaleString()}</p>
                        {f.year && <p className="text-xs text-gray-500">{f.year}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-3 mt-3 text-xs text-gray-600">
                      <div><span className="text-gray-400 block">Engine</span><span className="font-medium capitalize">{f.engine_type}</span></div>
                      <div><span className="text-gray-400 block">Capacity</span><span className="font-medium font-mono">{f.capacity_lbs?.toLocaleString()} lbs</span></div>
                      <div><span className="text-gray-400 block">Mast</span><span className="font-medium">{f.mast_stages} stages</span></div>
                      <div><span className="text-gray-400 block">Tires</span><span className="font-medium capitalize">{f.tire_type}</span></div>
                      <div><span className="text-gray-400 block">Drive</span><span className="font-medium uppercase">{f.drive}</span></div>
                    </div>
                    {f.hours_used != null && <p className="text-xs text-gray-400 mt-2 font-mono">{f.hours_used?.toLocaleString()} hours</p>}
                  </div>
                  <a href={`/inventory/${f.id}`} target="_blank" rel="noreferrer" className="p-2 text-gray-400 hover:text-blue-600 flex-shrink-0">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick contact */}
      {hasContact && (
        <div className="flex gap-3">
          {req.phone && (
            <a href={`tel:${req.phone}`} className="inline-flex items-center gap-2 bg-green-50 text-green-800 hover:bg-green-100 border border-green-200 px-4 py-2 rounded-sm text-sm font-medium">
              <Phone className="w-4 h-4" /> Call {req.phone}
            </a>
          )}
          {req.email && !req.email.includes("@noemail") && (
            <a href={`mailto:${req.email}`} className="inline-flex items-center gap-2 bg-blue-50 text-blue-800 hover:bg-blue-100 border border-blue-200 px-4 py-2 rounded-sm text-sm font-medium">
              <Mail className="w-4 h-4" /> Email {req.email}
            </a>
          )}
        </div>
      )}

      {/* Send-to-client modal */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="send-modal" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send forklifts to {req.name}</DialogTitle>
            <DialogDescription>
              Selecciona las máquinas, idioma del mensaje y el canal de envío.
            </DialogDescription>
          </DialogHeader>

          {!result ? (
            <div className="space-y-5">
              {/* Language */}
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block">Language</label>
                <div className="flex gap-2">
                  {["fr", "en"].map((l) => (
                    <button
                      key={l}
                      data-testid={`send-lang-${l}`}
                      onClick={() => setSendLang(l)}
                      className={`px-4 py-2 rounded-sm text-sm font-semibold border ${sendLang === l ? "bg-yellow-400 border-yellow-500 text-gray-900" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      {l === "fr" ? "Français" : "English"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Channel */}
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 block">Channel</label>
                <div className="flex gap-2">
                  <button
                    data-testid="send-channel-email"
                    onClick={() => setSendChannel("email")}
                    disabled={!hasEmail}
                    className={`flex-1 px-4 py-3 rounded-sm text-sm font-semibold border inline-flex items-center gap-2 justify-center ${sendChannel === "email" ? "bg-yellow-400 border-yellow-500 text-gray-900" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"} disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    <Mail className="w-4 h-4" /> Email
                    {!hasEmail && <span className="text-[10px] text-red-500">(no email)</span>}
                  </button>
                  <button
                    data-testid="send-channel-link"
                    onClick={() => setSendChannel("link")}
                    className={`flex-1 px-4 py-3 rounded-sm text-sm font-semibold border inline-flex items-center gap-2 justify-center ${sendChannel === "link" ? "bg-yellow-400 border-yellow-500 text-gray-900" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                  >
                    <Link2 className="w-4 h-4" /> Shareable link
                  </button>
                </div>
                {sendChannel === "email" && hasEmail && (
                  <p className="text-xs text-gray-500 mt-1">To: {req.email}</p>
                )}
              </div>

              {/* Forklift selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Forklifts ({selectedIds.size} / {matches.length})
                  </label>
                  <div className="flex gap-2 text-xs">
                    <button onClick={() => setSelectedIds(new Set(matches.map(m => m.id)))} className="text-blue-600 hover:underline">All</button>
                    <span className="text-gray-300">|</span>
                    <button onClick={() => setSelectedIds(new Set())} className="text-blue-600 hover:underline">None</button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-sm divide-y divide-gray-100">
                  {matches.map((m) => {
                    const checked = selectedIds.has(m.id);
                    return (
                      <label
                        key={m.id}
                        data-testid={`send-select-${m.id}`}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${checked ? "bg-yellow-50" : ""}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleSelect(m.id)} className="w-4 h-4" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{m.brand} {m.model}</p>
                          <p className="text-xs text-gray-500">{m.condition} · {m.capacity_lbs?.toLocaleString()} lbs · ${m.price?.toLocaleString()}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setSendOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-sm">Cancel</button>
                <button
                  data-testid="send-confirm-btn"
                  onClick={doSend}
                  disabled={sending || selectedIds.size === 0}
                  className="bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-5 py-2 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" /> {sending ? "Sending..." : (sendChannel === "email" ? "Send email" : "Generate link")}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-green-900 text-sm">
                    {result.email_sent ? `Email envoyé à ${req.email}` : "Lien généré"}
                  </p>
                </div>
                {result.share_url && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 mb-1">Shareable link (valid forever):</p>
                    <div className="flex gap-2">
                      <input
                        readOnly
                        value={result.share_url}
                        className="flex-1 text-xs font-mono bg-white border border-gray-200 rounded-sm px-2 py-1.5"
                        onFocus={(e) => e.target.select()}
                      />
                      <button
                        data-testid="copy-link-btn"
                        onClick={copyLink}
                        className="inline-flex items-center gap-1 bg-gray-900 text-white text-xs font-semibold px-3 py-1.5 rounded-sm hover:bg-gray-800"
                      >
                        {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setResult(null); setSendOpen(false); }} className="bg-gray-900 text-white px-5 py-2 rounded-sm text-sm font-bold hover:bg-gray-800">Done</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
